/**
 * Types de la disponibilité des médecins, miroir du contrat serveur
 * (`lacompassion-server/tasks/todo.md`, « Contrat d'API »). Heures `HH:mm`,
 * dates `yyyy-MM-dd`.
 */

export type DayOfWeek =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";

/** Jours dans l'ordre d'affichage (semaine française, lundi en tête). */
export const DAYS_OF_WEEK: { value: DayOfWeek; label: string }[] = [
  { value: "MONDAY", label: "Lundi" },
  { value: "TUESDAY", label: "Mardi" },
  { value: "WEDNESDAY", label: "Mercredi" },
  { value: "THURSDAY", label: "Jeudi" },
  { value: "FRIDAY", label: "Vendredi" },
  { value: "SATURDAY", label: "Samedi" },
  { value: "SUNDAY", label: "Dimanche" },
];

export interface WeeklyAvailability {
  id: number;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}

export interface Absence {
  id: number;
  startDate: string;
  endDate: string;
  reason: string | null;
}

/** `GET /availability/doctors/{doctorId}`. */
export interface DoctorAvailability {
  doctorId: number;
  /** `false` : aucune semaine type, le serveur applique la grille par défaut (D2). */
  configured: boolean;
  slotMinutes: number;
  weekly: WeeklyAvailability[];
  absences: Absence[];
}

/** Créneau libre (`GET /availability/doctors/{doctorId}/slots`). */
export interface Slot {
  date: string;
  startTime: string;
  endTime: string;
}

export interface WeeklyAvailabilityRequest {
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}

export interface AbsenceRequest {
  startDate: string;
  endDate: string;
  reason?: string | null;
}

/**
 * État candidat = actuel − `replacedWeeklyId` − `replacedAbsenceId`
 * + `weekly` + `absence`.
 */
export interface AvailabilityImpactRequest {
  replacedWeeklyId?: number | null;
  /** Modification d'une absence : l'ancienne période n'est plus comptée. */
  replacedAbsenceId?: number | null;
  weekly?: WeeklyAvailabilityRequest | null;
  absence?: AbsenceRequest | null;
}

/** RDV futur qui sortirait de la disponibilité après le changement. */
export interface ImpactedAppointment {
  consultationId: number;
  patientId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  appointmentDate: string;
  appointmentTime: string;
}

/** Durée d'un créneau quand la disponibilité n'est pas encore chargée (D1). */
export const DEFAULT_SLOT_MINUTES = 15;

/**
 * Grille appliquée par le serveur à un médecin sans aucune plage (décision D2),
 * telle qu'on l'annonce à l'écran.
 */
export const DEFAULT_GRID_LABEL = "de 8 h à 18 h tous les jours";

/** Plage maximale de `GET /slots` (contrôlée côté serveur). */
export const SLOTS_MAX_RANGE_DAYS = 31;

/** Longueur maximale du motif d'absence, alignée sur la validation serveur. */
export const ABSENCE_REASON_MAX_LENGTH = 120;
